import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../api/client.js';
import { useCurrentUser } from '../hooks/useCurrentUser.js';
import { Button, Table, TableCell, TableHeader, TableRow } from './ds';
import { FilterDropdown } from './FilterDropdown.jsx';
import './monitoring2.css';

const GEO_ROLES = ['ADMIN', 'TEAM_LEAD', 'MANAGER', 'ASSISTANT'];
const CAPTION = 'Оценка по последнему распознанному скрину каждого аккаунта. Доли применены к приросту просмотров за выбранные даты; периоды скринов могут отличаться. Сумма долей может отличаться от 100% из-за округления.';
const NEG_GEO = 'Есть отрицательные корректировки просмотров у аккаунтов с распознанным гео; доли в таблице и процент покрытия не рассчитаны';
const NEG_ALL = 'Есть отрицательные корректировки просмотров у аккаунтов без гео; процент покрытия не рассчитан. Доли в таблице — среди аккаунтов с распознанным гео';

const intFmt = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });
const pctFmt = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
export const fmtViews = (v) => (v == null ? '—' : v !== 0 && Math.abs(v) < 0.5 ? `${v < 0 ? '-' : ''}<1` : intFmt.format(v));
export const fmtPct = (p) => (p == null ? '—' : p !== 0 && Math.abs(p) < 0.05 ? `${p < 0 ? '-' : ''}<0,1` : pctFmt.format(p));

export function useGeoReport (params, enabled = true) {
  const { user } = useCurrentUser();
  const query = useQuery({
    queryKey: ['monitoring2', 'geo', user?.id, user?.role, user?.teamId, params],
    enabled: enabled && Boolean(user) && GEO_ROLES.includes(user.role),
    queryFn: () => apiClient.get('/monitoring2/geo', { params }).then((r) => r.data),
    staleTime: 0, gcTime: 0, retry: false, refetchOnWindowFocus: false,
  });
  return query;
}

/** Rows as shown: the card keeps `limit` named countries and folds the rest into one tail row. */
function displayRows (rows, limit) {
  const named = rows.filter((r) => r.kind === 'country');
  if (!limit || named.length <= limit) return rows;
  const rest = named.slice(limit);
  const tail = { kind: 'tail', name: 'Остальные страны', estimatedViews: rest.reduce((s, r) => s + r.estimatedViews, 0),
    pct: rest.some((r) => r.pct == null) ? null : rest.reduce((s, r) => s + r.pct, 0) };
  return [...named.slice(0, limit), tail, ...rows.filter((r) => r.kind !== 'country')];
}

function geoMessage (data) {
  const { coverage, negativeWeights } = data;
  if (negativeWeights.geo) return NEG_GEO;
  if (negativeWeights.all) return NEG_ALL;
  if (coverage.accounts === 0) return 'Нет аккаунтов в выбранной выборке';
  if (coverage.geoAccounts === 0) return 'Нет распознанного гео';
  if (coverage.coveredViews === 0) return 'Нет прироста просмотров за период';
  return null;
}

export const fmtDay = (iso) => (/^\d{4}-\d{2}-\d{2}$/.test(iso ?? '') ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : '');
const COLS = { views: 200, pct: 120 };

/** The shared presenter of both surfaces: coverage, table, messages, audiences. Only the card passes `limit` and gets the compact card layout. */
export function GeoTable ({ data, limit }) {
  const { coverage, window } = data;
  const card = limit != null;
  const message = geoMessage(data);
  const rows = displayRows(data.rows, limit);
  const share = `${fmtPct(coverage.viewsPct)}${coverage.viewsPct == null ? '' : '%'}`;
  const note = card ? 'oc-lead-card__muted' : 'oc-m2-report__muted';
  const audience = data.audiences.length > 0 && (
    <details className={card ? 'oc-lead-card__muted' : 'oc-m2-report__packs'}>
      <summary className={card ? undefined : 'oc-m2-report__team-name'}>Аудитория на скринах:</summary>
      <ul>{data.audiences.map((a) => <li key={a.label ?? ''}>{`${a.label ?? 'Не указана'} — ${a.accounts}`}</li>)}</ul>
    </details>
  );
  const coverageLine = card ? (
    <div data-geo-coverage="" className="oc-lead-card__totals">
      <div>{`${fmtDay(window.startDate)} – ${fmtDay(window.endDate)}`}</div>
      <div>Гео есть у <b>{coverage.geoAccounts}</b> из <b>{coverage.accounts}</b> аккаунтов</div>
      <div>Просмотры с гео: <b>{fmtViews(coverage.coveredViews)}</b> из <b>{fmtViews(coverage.totalViews)}</b> · <b>{share}</b></div>
    </div>
  ) : (
    <div data-geo-coverage="" className="oc-m2-grand">
      <span className="oc-m2-grand__label">Покрытие гео</span>
      <span>Гео есть у <b>{coverage.geoAccounts}</b> из <b>{coverage.accounts}</b> аккаунтов</span>
      <span>Просмотры с гео: <b>{fmtViews(coverage.coveredViews)}</b> из <b>{fmtViews(coverage.totalViews)}</b> · <b>{share}</b></span>
    </div>
  );
  return (
    <>
      {coverageLine}
      <p className={note}>{CAPTION}</p>
      {message && <p className={note}>{message}</p>}
      {rows.length > 0 && (card ? (
        <table className="oc-lead-card__table">
          <thead>
            <tr><th>Страна</th><th className="oc-lead-card__num">Оценка просмотров ≈</th><th className="oc-lead-card__num">Доля, %</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.kind}-${r.iso2 ?? r.name}`} data-geo-row={r.kind}>
                <td>{r.name}</td>
                <td className="oc-lead-card__num">{fmtViews(r.estimatedViews)}</td>
                <td className="oc-lead-card__num">{fmtPct(r.pct)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <Table numbered={false}>
          <TableRow header>
            <TableHeader>Страна</TableHeader>
            <TableHeader align="right" width={COLS.views}>Оценка просмотров ≈</TableHeader>
            <TableHeader align="right" width={COLS.pct}>Доля, %</TableHeader>
          </TableRow>
          {rows.map((r) => (
            <TableRow key={`${r.kind}-${r.iso2 ?? r.name}`} data-geo-row={r.kind}>
              <TableCell>{r.name}</TableCell>
              <TableCell align="right" width={COLS.views}>{fmtViews(r.estimatedViews)}</TableCell>
              <TableCell align="right" width={COLS.pct}>{fmtPct(r.pct)}</TableCell>
            </TableRow>
          ))}
        </Table>
      ))}
      {audience}
      <p className={note}>Исходные скрины — в «Документы → Кабинет блогера».</p>
    </>
  );
}

/** Loading, error and result of one GEO query; a previous identity's numbers are never shown. */
export function GeoState ({ query, limit, windowMessage = 'Данные просмотров за выбранные даты ещё не готовы' }) {
  const card = limit != null;
  if (query.isError) {
    const unready = query.error?.data?.error === 'geo_window_unavailable';
    return (
      <div data-geo-error="" className={card ? `${unready ? 'oc-lead-card__muted' : 'oc-lead-card__error'} oc-lead-card__actions` : 'oc-m2-report__state'}>
        <span className={card ? undefined : 'oc-m2-report__error'}>{unready ? windowMessage : 'Не удалось загрузить гео'}</span>
        <Button hierarchy="outline" size="small" onClick={() => query.refetch()}>Повторить</Button>
      </div>
    );
  }
  if (!query.data) return <div className={card ? 'oc-lead-card__muted' : 'oc-m2-report__muted'}>Загрузка гео…</div>;
  return <GeoTable data={query.data} limit={limit} />;
}

const FILTERS = [['application', 'Все приложения', 'applications'], ['manager', 'Все менеджеры', 'managers'], ['team', 'Все команды', 'teams']];

export default function MonitoringGeoView () {
  const { user } = useCurrentUser();
  const [params, setParams] = useSearchParams();
  const get = (k) => params.get(k) || undefined;
  const request = Object.fromEntries(['startDate', 'endDate', 'packId', 'application', 'manager', 'team'].map((k) => [k, params.getAll(k).join(',') || undefined]).filter(([, v]) => v));
  const query = useGeoReport(request);
  const options = useRef({});
  const pack = useRef(null);
  if (query.data) { options.current = query.data.options; pack.current = query.data.pack; }
  if (!request.packId) pack.current = null;

  const resolved = query.data?.window;
  useEffect(() => {
    if (resolved && !params.get('startDate')) {
      setParams((prev) => { const next = new URLSearchParams(prev); next.set('startDate', resolved.startDate); next.set('endDate', resolved.endDate); return next; }, { replace: true });
    }
  }, [resolved, params, setParams]);
  if (user && !GEO_ROLES.includes(user.role)) return <p>Нет доступа к гео-отчёту.</p>;
  const set = (patch) => setParams((prev) => {
    const next = new URLSearchParams(prev);
    for (const [k, v] of Object.entries(patch)) { if (v) next.set(k, v); else next.delete(k); }
    return next;
  }, { replace: true });
  const start = params.get('startDate') ?? resolved?.startDate ?? '';
  const end = params.get('endDate') ?? resolved?.endDate ?? '';

  return (
    <section data-geo-report="" className="outcrm-scope oc-m2-page" aria-busy={query.isFetching && !query.isError}>
      <div className="oc-m2-report oc-m2-prep">
        <h2>Гео</h2>
        <div className="oc-m2-toolbar">
          {FILTERS.map(([name, all, key]) => (
            <div key={name} aria-label={all}>
              <FilterDropdown value={params.get(name) ?? ''} placeholder={all} onChange={(value) => set({ [name]: value })}
                options={[{ value: '', label: all }, ...(options.current[key] ?? [])]} />
            </div>
          ))}
          <Button hierarchy="outline" size="small" onClick={() => query.refetch()}>Обновить</Button>
        </div>
        <div className="oc-m2-report__head">
          <label>Старт <input type="date" name="startDate" value={start} onChange={(e) => e.target.value && set({ startDate: e.target.value, endDate: end })} /></label>
          <label>Конец <input type="date" name="endDate" value={end} onChange={(e) => e.target.value && set({ startDate: start, endDate: e.target.value })} /></label>
        </div>
        {fmtDay(start) && fmtDay(end) && <p className="oc-m2-report__muted">просмотры за {fmtDay(start)}–{fmtDay(end)}</p>}
        {request.packId && pack.current?.id === request.packId && (
          <div data-geo-pack="" className="oc-m2-report__state">Выбрана пачка: {pack.current.label} <Button hierarchy="outline" size="small" onClick={() => set({ packId: '' })}>Сбросить</Button></div>
        )}
        <GeoState query={query} />
      </div>
    </section>
  );
}
